import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import Avatar from "../components/Avatar";
import SegmentedToggle from "../components/SegmentedToggle";
import DmBubble from "../components/DmBubble";
import DmFlagsList from "../components/DmFlagsList";
import { TagChip } from "../components/Pills";
import { ErrorBanner, LoadingNote } from "../components/Layout";
import { useDashboardData } from "../hooks/useDashboardData";
import { usePagedFeed } from "../hooks/usePagedFeed";
import {
  fetchDmFlags,
  fetchDmAccountActivity,
  fetchDmMessages,
  fetchDmThreads,
  fetchDmThreadUsername,
  fetchInstagramAccounts,
} from "../dashboardApi";
import {
  DmAccountActivityResponse,
  DmFlagRow,
  DmMessageRow,
  DmThreadRow,
  InstagramAccountRow,
  InstagramAccountsResponse,
} from "../types";
import { card, colors, type } from "../styles";
import { formatAgo, formatDateTime } from "../format";

/**
 * Chat-style viewer for the Instagram DM threads Markit has stored.
 *
 * Three columns, left to right: the connected creator accounts, the selected
 * account's threads, and the selected thread's messages. A second view lists
 * the anomaly flags raised across every creator, so "did anything go wrong
 * recently" is one click, and each flag opens the thread it came from.
 *
 * Only multi-step flows and failed sends are stored, and each expires after 90 days.
 * That is stated on the page rather than left for a reader to wonder why an
 * old or purely-organic conversation is missing.
 *
 * Selection lives in the URL (`?account=<uid>:<igAccountId>&thread=<igUserId>`,
 * `&tab=flags`) so a thread can be linked to in Slack or a ticket. The account
 * list is the same live endpoint the Instagram screen uses; threads, messages
 * and flags are cursor-paged and fetched only when asked for, which is why this
 * is a page of its own rather than a section of the Instagram tab.
 */

type Tab = "threads" | "flags";

const TAB_OPTIONS: { key: Tab; label: string }[] = [
  { key: "threads", label: "Threads" },
  { key: "flags", label: "Flags" },
];

const SOURCE_PREFIX: Record<string, string> = {
  markit: "Markit: ",
  creator: "Creator: ",
  fan: "",
};

const columnHeaderStyle = {
  ...type.overline,
  color: colors.inkFaint,
  padding: "14px 14px 8px",
} as const;

const rowButtonStyle = (selected: boolean) =>
  ({
    display: "block",
    width: "100%",
    textAlign: "left",
    background: selected ? colors.surfaceMuted : "transparent",
    border: "none",
    borderBottom: `1px solid ${colors.border}`,
    padding: "10px 14px",
    cursor: "pointer",
  } as const);

const smallButtonStyle = (disabled: boolean) =>
  ({
    ...type.bodySmallMedium,
    color: colors.inkMuted,
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 6,
    padding: "6px 12px",
    cursor: disabled ? "default" : "pointer",
  } as const);

const EmptyNote = ({ children }: { children: string }) => (
  <div style={{ ...type.bodyRegular, color: colors.inkMuted, padding: "16px 14px" }}>
    {children}
  </div>
);

const parseAccountParam = (value: string | null) => {
  if (!value) return undefined;
  const [uid, igAccountId] = value.split(":");
  return uid && igAccountId ? { uid, igAccountId } : undefined;
};

const dayKey = (iso: string) => new Date(iso).toDateString();

const InstagramDmsScreen = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: Tab = searchParams.get("tab") === "flags" ? "flags" : "threads";
  const accountParam = searchParams.get("account");
  const account = parseAccountParam(accountParam);
  const threadId = searchParams.get("thread") ?? undefined;
  const [query, setQuery] = useState("");

  const select = useCallback(
    (updates: Record<string, string | undefined>) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) next.set(key, value);
        else next.delete(key);
      });
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const accountsFetcher = useCallback(() => fetchInstagramAccounts(), []);
  const accounts = useDashboardData<InstagramAccountsResponse>(accountsFetcher, []);

  // When each account last got a stored DM. Failing to load it only drops the labels and the
  // sorting, so it has no banner of its own.
  const activityFetcher = useCallback(() => fetchDmAccountActivity(), []);
  const activity = useDashboardData<DmAccountActivityResponse>(activityFetcher, []);
  const lastDmByAccount = useMemo(
    () =>
      new Map(
        (activity.data?.accounts ?? []).map(row => [`${row.uid}:${row.igAccountId}`, row])
      ),
    [activity.data]
  );

  // Accounts that got a DM most recently first; the rest keep the order they came in
  const filteredAccounts = useMemo(() => {
    const rows = accounts.data?.accounts ?? [];
    const needle = query.trim().toLowerCase();
    const matching = needle
      ? rows.filter(row =>
          `${row.creatorName} ${row.creatorUsername} ${row.instagramUsername}`
            .toLowerCase()
            .includes(needle)
        )
      : rows;
    const lastDmAt = (row: InstagramAccountRow) =>
      lastDmByAccount.get(`${row.creatorUid}:${row.instagramAccountId}`)?.lastMessageAt ?? "";
    return [...matching].sort((a, b) => lastDmAt(b).localeCompare(lastDmAt(a)));
  }, [accounts.data, query, lastDmByAccount]);

  const selectedAccount: InstagramAccountRow | undefined = useMemo(
    () =>
      accounts.data?.accounts.find(
        row =>
          row.creatorUid === account?.uid && row.instagramAccountId === account?.igAccountId
      ),
    [accounts.data, account?.uid, account?.igAccountId]
  );

  const threads = usePagedFeed<DmThreadRow>(
    cursor =>
      fetchDmThreads(account!.uid, account!.igAccountId, cursor).then(page => ({
        items: page.threads,
        nextCursor: page.nextCursor,
      })),
    [account?.uid, account?.igAccountId],
    Boolean(account)
  );

  const messages = usePagedFeed<DmMessageRow>(
    cursor =>
      fetchDmMessages(account!.uid, account!.igAccountId, threadId!, cursor).then(page => ({
        items: page.messages,
        nextCursor: page.nextCursor,
      })),
    [account?.uid, account?.igAccountId, threadId],
    Boolean(account && threadId)
  );

  const flags = usePagedFeed<DmFlagRow>(
    cursor =>
      fetchDmFlags(cursor).then(page => ({ items: page.flags, nextCursor: page.nextCursor })),
    [],
    tab === "flags"
  );

  const selectedThread = threads.items.find(thread => thread.igUserId === threadId);

  // A thread started by a DM has no username, so one is looked up when an admin opens it. The
  // backend stores it on the thread; it is kept here too so the list and header update at once.
  // Each thread is only asked about once per visit, so a failed lookup is not retried in a loop.
  const [resolvedUsernames, setResolvedUsernames] = useState<Record<string, string>>({});
  const askedForUsername = useRef(new Set<string>());
  useEffect(() => {
    if (!account || !threadId || selectedThread?.igUsername) return;
    const key = `${account.uid}:${account.igAccountId}:${threadId}`;
    if (askedForUsername.current.has(key)) return;
    askedForUsername.current.add(key);
    fetchDmThreadUsername(account.uid, account.igAccountId, threadId)
      .then(({ igUsername }) => {
        if (igUsername) setResolvedUsernames(prev => ({ ...prev, [key]: igUsername }));
      })
      .catch(() => undefined);
  }, [account, threadId, selectedThread?.igUsername]);
  const usernameOf = (row: DmThreadRow) =>
    row.igUsername ??
    (account ? resolvedUsernames[`${account.uid}:${account.igAccountId}:${row.igUserId}`] : undefined);

  // The API returns newest first; a chat reads oldest to newest.
  const orderedMessages = useMemo(() => [...messages.items].reverse(), [messages.items]);

  // Scroll to the newest message when a thread opens, and hold the reader's
  // place when "Load earlier messages" prepends older ones above it.
  const scrollRef = useRef<HTMLDivElement>(null);
  const heightBeforeLoad = useRef<number | null>(null);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (heightBeforeLoad.current !== null) {
      el.scrollTop += el.scrollHeight - heightBeforeLoad.current;
      heightBeforeLoad.current = null;
    } else {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages.items.length, threadId]);

  const loadEarlier = useCallback(() => {
    heightBeforeLoad.current = scrollRef.current?.scrollHeight ?? null;
    messages.loadMore();
  }, [messages]);

  const selectAccount = (row: InstagramAccountRow) => {
    heightBeforeLoad.current = null;
    select({ account: `${row.creatorUid}:${row.instagramAccountId}`, thread: undefined });
  };

  const selectThread = (row: DmThreadRow) => {
    heightBeforeLoad.current = null;
    select({ thread: row.igUserId });
  };

  const openFlag = (flag: DmFlagRow) => {
    heightBeforeLoad.current = null;
    select({
      tab: undefined,
      account: `${flag.creatorUid}:${flag.igAccountId}`,
      thread: flag.fanIgUserId,
    });
  };

  const selectedUsername = selectedThread
    ? usernameOf(selectedThread)
    : account && threadId
    ? resolvedUsernames[`${account.uid}:${account.igAccountId}:${threadId}`]
    : undefined;
  const fanName = selectedUsername ? `@${selectedUsername}` : threadId ?? "";

  return (
    <div>
      <PageHeader
        title="DM Threads"
        subtitle="Multi-step flows and failed sends are stored for 90 days. A single DM that sent fine is not."
        right={
          <SegmentedToggle<Tab>
            options={TAB_OPTIONS}
            value={tab}
            onChange={next => select({ tab: next === "flags" ? "flags" : undefined })}
            label="View"
            size="default"
          />
        }
      />

      {accounts.error && <ErrorBanner message={accounts.error} />}

      <div
        style={{
          ...card,
          display: "flex",
          height: "calc(100vh - 210px)",
          minHeight: 520,
          overflow: "hidden",
        }}
      >
        {tab === "flags" ? (
          <DmFlagsList feed={flags} onSelect={openFlag} />
        ) : (
          <>
            {/* Creator accounts */}
            <div
              style={{
                width: 250,
                flexShrink: 0,
                borderRight: `1px solid ${colors.border}`,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              <div style={columnHeaderStyle}>Creator accounts</div>
              <div style={{ padding: "0 14px 10px" }}>
                <input
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                  placeholder="Search accounts"
                  aria-label="Search accounts"
                  style={{
                    ...type.bodyRegular,
                    width: "100%",
                    boxSizing: "border-box",
                    color: colors.ink,
                    background: colors.surface,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 8,
                    padding: "7px 10px",
                    outline: "none",
                  }}
                />
              </div>
              <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
                {accounts.loading && (
                  <div style={{ padding: "0 14px" }}>
                    <LoadingNote />
                  </div>
                )}
                {!accounts.loading && filteredAccounts.length === 0 && (
                  <EmptyNote>No matching accounts.</EmptyNote>
                )}
                {filteredAccounts.map(row => {
                  const selected =
                    row.creatorUid === account?.uid &&
                    row.instagramAccountId === account?.igAccountId;
                  const lastDm = lastDmByAccount.get(
                    `${row.creatorUid}:${row.instagramAccountId}`
                  );
                  return (
                    <button
                      key={`${row.creatorUid}:${row.instagramAccountId}`}
                      onClick={() => selectAccount(row)}
                      aria-pressed={selected}
                      style={{
                        ...rowButtonStyle(selected),
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <Avatar
                        url={row.profilePicUrl}
                        fullName={row.creatorName}
                        username={row.instagramUsername}
                        size={32}
                      />
                      <span style={{ minWidth: 0 }}>
                        <span
                          style={{
                            ...type.bodyBold,
                            color: colors.ink,
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.creatorName || row.instagramUsername}
                        </span>
                        <span
                          style={{
                            ...type.bodySmallRegular,
                            color: colors.inkMuted,
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          @{row.instagramUsername}
                        </span>
                        {lastDm && (
                          <span
                            title={formatDateTime(lastDm.lastMessageAt)}
                            style={{
                              ...type.bodySmallMedium,
                              color:
                                Date.now() - new Date(lastDm.lastMessageAt).getTime() <
                                24 * 60 * 60 * 1000
                                  ? colors.stateActive
                                  : colors.inkFaint,
                              display: "block",
                            }}
                          >
                            Last DM {formatAgo(lastDm.lastMessageAt)}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Threads */}
            <div
              style={{
                width: 310,
                flexShrink: 0,
                borderRight: `1px solid ${colors.border}`,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              <div style={columnHeaderStyle}>Threads · newest first</div>
              <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
                {!account && <EmptyNote>Select an account to see its threads.</EmptyNote>}
                {threads.error && (
                  <div style={{ padding: "0 14px" }}>
                    <ErrorBanner message={threads.error} />
                  </div>
                )}
                {threads.loading && (
                  <div style={{ padding: "0 14px" }}>
                    <LoadingNote />
                  </div>
                )}
                {account && !threads.loading && !threads.error && threads.items.length === 0 && (
                  <EmptyNote>No DM threads stored for this account yet.</EmptyNote>
                )}
                {threads.items.map(row => (
                  <button
                    key={row.igUserId}
                    onClick={() => selectThread(row)}
                    aria-pressed={row.igUserId === threadId}
                    style={rowButtonStyle(row.igUserId === threadId)}
                  >
                    <span style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                      <span
                        style={{
                          ...type.bodyBold,
                          color: colors.ink,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {usernameOf(row) ? `@${usernameOf(row)}` : row.igUserId}
                      </span>
                      <span
                        style={{
                          ...type.bodySmallRegular,
                          color: colors.inkFaint,
                          flexShrink: 0,
                        }}
                        title={formatDateTime(row.lastMessageAt)}
                      >
                        {formatAgo(row.lastMessageAt)}
                      </span>
                    </span>
                    <span
                      style={{
                        ...type.bodySmallRegular,
                        color: colors.inkMuted,
                        display: "block",
                        marginTop: 2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {SOURCE_PREFIX[row.lastMessageSource] ?? ""}
                      {row.lastMessageText}
                    </span>
                    {row.flagCount > 0 && (
                      <span style={{ display: "block", marginTop: 6 }}>
                        <TagChip
                          label={row.flagCount === 1 ? "1 flag" : `${row.flagCount} flags`}
                          tone="warning"
                        />
                      </span>
                    )}
                  </button>
                ))}
                {threads.hasMore && (
                  <div style={{ padding: 14, textAlign: "center" }}>
                    <button
                      onClick={threads.loadMore}
                      disabled={threads.loadingMore}
                      style={smallButtonStyle(threads.loadingMore)}
                    >
                      {threads.loadingMore ? "Loading…" : "Load more threads"}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Messages */}
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              {!threadId ? (
                <EmptyNote>
                  {account ? "Select a thread to read it." : "Select an account, then a thread."}
                </EmptyNote>
              ) : (
                <>
                  <div
                    style={{
                      padding: "14px 16px",
                      borderBottom: `1px solid ${colors.border}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ ...type.cardTitle, color: colors.ink }}>{fanName}</div>
                      <div style={{ ...type.bodySmallRegular, color: colors.inkMuted }}>
                        With{" "}
                        {selectedAccount
                          ? `${selectedAccount.creatorName} (@${selectedAccount.instagramUsername})`
                          : "the selected account"}
                      </div>
                    </div>
                    {selectedThread && selectedThread.flagCount > 0 && (
                      <button
                        onClick={() => select({ tab: "flags" })}
                        style={{ background: "transparent", border: "none", cursor: "pointer" }}
                        title="See this thread's flags in the Flags view"
                      >
                        <TagChip
                          label={
                            selectedThread.flagCount === 1
                              ? "1 flag · see Flags"
                              : `${selectedThread.flagCount} flags · see Flags`
                          }
                          tone="warning"
                        />
                      </button>
                    )}
                  </div>
                  <div
                    ref={scrollRef}
                    style={{
                      flex: 1,
                      minHeight: 0,
                      overflowY: "auto",
                      padding: 16,
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                    }}
                  >
                    {messages.hasMore && (
                      <div style={{ textAlign: "center" }}>
                        <button
                          onClick={loadEarlier}
                          disabled={messages.loadingMore}
                          style={smallButtonStyle(messages.loadingMore)}
                        >
                          {messages.loadingMore ? "Loading…" : "Load earlier messages"}
                        </button>
                      </div>
                    )}
                    {messages.error && <ErrorBanner message={messages.error} />}
                    {messages.loading && <LoadingNote />}
                    {!messages.loading && !messages.error && orderedMessages.length === 0 && (
                      <EmptyNote>No messages stored for this thread.</EmptyNote>
                    )}
                    {orderedMessages.map((message, index) => {
                      const previous = orderedMessages[index - 1];
                      const newDay =
                        !previous || dayKey(previous.sentAt) !== dayKey(message.sentAt);
                      return (
                        <div
                          key={message.mid}
                          style={{ display: "flex", flexDirection: "column", gap: 10 }}
                        >
                          {newDay && (
                            <div
                              style={{
                                ...type.bodySmallMedium,
                                color: colors.inkFaint,
                                textAlign: "center",
                              }}
                            >
                              {new Date(message.sentAt).toLocaleDateString("en-US", {
                                weekday: "short",
                                month: "short",
                                day: "numeric",
                              })}
                            </div>
                          )}
                          <DmBubble message={message} />
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InstagramDmsScreen;
